import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Button, Space, Tooltip } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LinkOutlined,
} from '@ant-design/icons';

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  height?: number;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  height = 200
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,  // Disable built-in Link from StarterKit
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        style: `min-height: ${height}px; padding: 12px;`,
        placeholder: placeholder || '',
      },
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6 }}>
      {/* Toolbar */}
      <Space
        style={{
          padding: 8,
          borderBottom: '1px solid #d9d9d9',
          background: '#fafafa',
          width: '100%',
        }}
      >
        <Tooltip title="粗體">
          <Button
            icon={<BoldOutlined />}
            size="small"
            type={editor.isActive('bold') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
        </Tooltip>
        <Tooltip title="斜體">
          <Button
            icon={<ItalicOutlined />}
            size="small"
            type={editor.isActive('italic') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
        </Tooltip>
        <Tooltip title="無序清單">
          <Button
            icon={<UnorderedListOutlined />}
            size="small"
            type={editor.isActive('bulletList') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
        </Tooltip>
        <Tooltip title="有序清單">
          <Button
            icon={<OrderedListOutlined />}
            size="small"
            type={editor.isActive('orderedList') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
        </Tooltip>
        <Tooltip title="插入連結">
          <Button
            icon={<LinkOutlined />}
            size="small"
            type={editor.isActive('link') ? 'primary' : 'default'}
            onClick={() => {
              const url = window.prompt('輸入連結 URL:');
              if (url) {
                editor.chain().focus().setLink({ href: url }).run();
              }
            }}
          />
        </Tooltip>
      </Space>

      {/* Editor Content */}
      <EditorContent
        editor={editor}
        style={{
          minHeight: height,
          maxHeight: 400,
          overflow: 'auto',
        }}
      />
    </div>
  );
}
